import type { DeliveryTarget, DmMedium } from './types.js';

/** Format a delivery target as a short human-readable label for the agent
 *  edit picker and schedule list views. Pure function — takes a resolved
 *  context (channel/person name lookups), returns a string. */
export interface FormatContext {
  /** Map of Slack channel id → `#channel-name`. */
  slack_channel_names?: Record<string, string>;
  /** Map of Telegram chat id → display name. */
  telegram_chat_names?: Record<string, string>;
  /** Map of person_id → display name. */
  people?: Record<string, string>;
}

export function formatDeliveryLabel(
  target: DeliveryTarget,
  ctx: FormatContext = {},
): string {
  if (target.kind === 'channel') {
    if (target.provider === 'slack') {
      const name = ctx.slack_channel_names?.[target.channel_id ?? ''];
      // ENG-6038: surface the thread coordinate so list/edit views can't
      // silently hide that a delivery threads back into a conversation.
      const thread = target.thread_ts ? ' (in thread)' : '';
      return name ? `Slack — ${name}${thread}` : `Slack — #${target.channel_id ?? '?'}${thread}`;
    }
    const name = ctx.telegram_chat_names?.[target.chat_id ?? ''];
    return name ? `Telegram — ${name}` : `Telegram chat ${target.chat_id ?? '?'}`;
  }
  // kind === 'dm'
  const personName = ctx.people?.[target.person_id] ?? 'person';
  const suffix = target.follow_reports_to ? ' (Reports-To)' : '';
  return `DM ${personName}${suffix}`;
}

/** Build the attribution footer appended to every DM body (§6).
 *
 *  Channels never get this footer — channel-level context (bot name,
 *  channel membership) already makes the sender visible.
 *
 *  Input is assumed already safe for the target medium's formatting; we
 *  don't re-escape here. Callers use this verbatim. */
export function formatDmFooter(
  teamName: string | null,
  agentDisplayName: string,
): string {
  const team = teamName?.trim() || 'unassigned team';
  return `— scheduled by ${team} / ${agentDisplayName}`;
}

/** Append the DM footer to a body with a blank line separator. Safe no-op
 *  if the body already ends with the footer (idempotent for retries).
 *
 *  Idempotence is checked against the trimmed message's *suffix* — not
 *  arbitrary substring membership — so a schedule whose output happens to
 *  quote an earlier attribution block still gets a fresh footer appended at
 *  the end. (§6 guardrail, per CR feedback.) */
export function appendDmFooter(
  body: string,
  teamName: string | null,
  agentDisplayName: string,
): string {
  const footer = formatDmFooter(teamName, agentDisplayName);
  const trimmed = body.replace(/\s+$/, '');
  if (trimmed.endsWith(footer)) return trimmed;
  return `${trimmed}\n\n${footer}`;
}

/** Render a `DeliveryTarget` back to the legacy string form accepted by
 *  `openclaw cron add --to <...>`. Only channel-targets survive this
 *  round-trip — DM targets throw because OpenClaw's cron engine can't
 *  resolve them today (ENG-4423 §9.1).
 *
 *  Also throws on malformed channel targets missing the required ID, per
 *  CR #3108398206 — serialising `channel:` or `chat:` with an empty suffix
 *  turns bad state into a syntactically valid CLI flag and defers the
 *  failure downstream. */
export function formatForOpenClawCli(target: DeliveryTarget): string {
  if (target.kind === 'channel') {
    if (target.provider === 'slack') {
      if (!target.channel_id) {
        throw new Error('INVALID_DELIVERY_TARGET: slack channel target is missing channel_id');
      }
      // ENG-6038: the legacy string form has no thread slot — OpenClaw cron
      // can't thread, so a thread_ts is intentionally dropped here and the
      // delivery degrades to a top-level channel post.
      return `channel:${target.channel_id}`;
    }
    if (!target.chat_id) {
      throw new Error('INVALID_DELIVERY_TARGET: telegram channel target is missing chat_id');
    }
    return `chat:${target.chat_id}`;
  }
  throw new Error(
    `DM_NOT_SUPPORTED_ON_FRAMEWORK: dm targets can't be passed to openclaw cron add. See ENG-4423 §9.1 and the follow-up ENG-4431.`,
  );
}

/** Human-readable label for a DM medium (used in subtitle chips). */
export function formatMediumLabel(medium: DmMedium): string {
  if (medium === 'auto') return 'auto';
  if (medium === 'slack') return 'Slack';
  return 'Telegram';
}
